/**
 * Modal dédiée pour la prise de besoin "Politique de cybersécurité v2"
 * Cette modal charge dynamiquement les phases du template et sauvegarde dans phase1_data, phase2_data, etc.
 */

import { useState, useEffect, useMemo } from 'react';
import { X, ChevronLeft, ChevronRight, CheckCircle2, Shield, Lock, Building2 } from 'lucide-react';
import { createBesoin, updateBesoin, getBesoinById } from '../services/besoinsService';
import { getAllClients } from '../services/clientService';
import { getIntakeTemplate } from '../domain/besoins/templates';
import type { ClientItem } from '../services/clientService';

interface PriseDeBesoinPolitiqueCybersecuriteModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId?: number;
  templateId?: string; // 'politique-cybersecurite-v2'
  templateVersion?: string;
  besoinId?: number;
  onSuccess?: (besoinId: number) => void;
}

// Mapping des icônes par nom
const iconMap: Record<string, any> = {
  Building2,
  Shield,
  Lock,
};

export default function PriseDeBesoinPolitiqueCybersecuriteModal({
  isOpen,
  onClose,
  clientId = 2,
  templateId = 'politique-cybersecurite-v2',
  templateVersion = '2.0.0',
  besoinId,
  onSuccess,
}: PriseDeBesoinPolitiqueCybersecuriteModalProps) {
  const [step, setStep] = useState<number>(1);
  const [responses, setResponses] = useState<Record<string, string | string[]>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [nomBesoin, setNomBesoin] = useState<string>('');
  const [descriptionBesoin, setDescriptionBesoin] = useState<string>('');
  const [selectedClientId, setSelectedClientId] = useState<number | ''>(clientId || '');
  const [clients, setClients] = useState<ClientItem[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [showPriseClient, setShowPriseClient] = useState<boolean>(true);
  const [makeBy, setMakeBy] = useState<string>('');
  const [archived, setArchived] = useState<boolean>(false);

  // Charger le template
  const template = useMemo(() => {
    try {
      return getIntakeTemplate(templateId);
    } catch (error) {
      console.error('Erreur lors du chargement du template:', error);
      return null;
    }
  }, [templateId]);

  // Nombre de phases du template
  const maxSteps = useMemo(() => {
    return template?.phases.length || 0;
  }, [template]);

  // Phase actuelle
  const currentPhase = useMemo(() => {
    if (!template || step < 1 || step > template.phases.length) return null;
    return template.phases[step - 1];
  }, [template, step]);

  // Charger les données existantes au montage
  useEffect(() => {
    if (isOpen) {
      loadClients();
      if (besoinId) {
        loadExistingData();
      } else {
        // Réinitialiser le formulaire pour une nouvelle prise de besoin
        setResponses({});
        setNomBesoin('');
        setDescriptionBesoin('');
        setSelectedClientId(clientId || '');
        setStep(1);
      }
    }
  }, [isOpen, besoinId, clientId]);

  const loadClients = async () => {
    setIsLoadingClients(true);
    try {
      const clientsList = await getAllClients();
      const activeClients = clientsList
        .filter((c) => c.actif !== false)
        .sort((a, b) => {
          const nomA = a.nom?.toLowerCase() || '';
          const nomB = b.nom?.toLowerCase() || '';
          return nomA.localeCompare(nomB, 'fr', { sensitivity: 'base' });
        });
      setClients(activeClients);
    } catch (error) {
      console.error('Erreur chargement clients:', error);
    } finally {
      setIsLoadingClients(false);
    }
  };

  const loadExistingData = async () => {
    if (!besoinId) return;
    setIsLoading(true);
    try {
      const data = await getBesoinById(besoinId);
      if (data) {
        setNomBesoin(data.nom || '');
        setDescriptionBesoin(data.description || '');
        setSelectedClientId(data.client_id || '');
        setShowPriseClient(data.showpriseclient ?? true);
        setMakeBy(data.makeby || '');
        setArchived(data.archived ?? false);

        // Charger les réponses de toutes les phases
        const loadedResponses: Record<string, string | string[]> = {};
        
        // Phase 1
        if (data.phase1_data) {
          Object.assign(loadedResponses, data.phase1_data);
        }
        // Phase 2
        if (data.phase2_data) {
          Object.assign(loadedResponses, data.phase2_data);
        }
        // Phase 3
        if (data.phase3_data) {
          Object.assign(loadedResponses, data.phase3_data);
        }
        // Phase 4
        if (data.phase4_data) {
          Object.assign(loadedResponses, data.phase4_data);
        }
        // Phases supplémentaires si nécessaire
        if (data.phase5_data) {
          Object.assign(loadedResponses, data.phase5_data);
        }
        if (data.phase6_data) {
          Object.assign(loadedResponses, data.phase6_data);
        }
        if (data.phase7_data) {
          Object.assign(loadedResponses, data.phase7_data);
        }
        if (data.phase8_data) {
          Object.assign(loadedResponses, data.phase8_data);
        }
        if (data.phase9_data) {
          Object.assign(loadedResponses, data.phase9_data);
        }
        if (data.phase10_data) {
          Object.assign(loadedResponses, data.phase10_data);
        }

        setResponses(loadedResponses);
      }
    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResponse = (questionId: string, value: string | string[]) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Organiser les réponses par phase
      const phaseData: Record<string, Record<string, string | string[]>> = {};
      
      if (!template) {
        alert('Erreur: Template non chargé');
        setIsSaving(false);
        return;
      }

      // Séparer les réponses par phase selon les questions du template
      template.phases.forEach((phase, phaseIndex) => {
        const phaseNum = phaseIndex + 1;
        phaseData[`phase${phaseNum}_data`] = {};
        
        phase.questions.forEach((question) => {
          if (responses[question.id] !== undefined) {
            phaseData[`phase${phaseNum}_data`][question.id] = responses[question.id];
          }
        });
      });

      // Calculer les phases complétées
      const completedPhases: string[] = [];
      Object.keys(phaseData).forEach((phaseKey) => {
        if (Object.keys(phaseData[phaseKey]).length > 0) {
          completedPhases.push(phaseKey.replace('_data', ''));
        }
      });

      const completionPercentage = Math.round((completedPhases.length / maxSteps) * 100);

      // Valider qu'un client est sélectionné
      if (!selectedClientId || typeof selectedClientId !== 'number') {
        alert('Veuillez sélectionner un client');
        setIsSaving(false);
        return;
      }

      // Construire l'objet de données avec toutes les phases
      const besoinData: any = {
        client_id: selectedClientId as number,
        nom: nomBesoin || `Prise de besoin Politique de cybersécurité ${new Date().toLocaleDateString('fr-CA')}`,
        description: descriptionBesoin,
        template_id: templateId,
        template_version: templateVersion,
        statut: (completionPercentage === 100 ? 'complet' : 'en_cours') as 'complet' | 'en_cours',
        metadata: {
          completed_phases: completedPhases,
          completion_percentage: completionPercentage,
          last_modified_phase: `phase${step}`,
        },
        showpriseclient: showPriseClient,
        makeby: makeBy || null,
        archived,
      };

      // Ajouter les données de chaque phase
      Object.keys(phaseData).forEach((phaseKey) => {
        const phaseDataObj = phaseData[phaseKey];
        if (Object.keys(phaseDataObj).length > 0) {
          besoinData[phaseKey] = phaseDataObj;
        }
      });

      let savedBesoin;
      if (besoinId) {
        savedBesoin = await updateBesoin(besoinId, besoinData);
      } else {
        savedBesoin = await createBesoin(besoinData);
      }

      if (onSuccess && savedBesoin.id) {
        onSuccess(savedBesoin.id);
      }
      
      onClose();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      alert('Erreur lors de la sauvegarde. Veuillez réessayer.');
    } finally {
      setIsSaving(false);
    }
  };

  const goNext = () => {
    if (step < maxSteps) {
      setStep(step + 1);
    }
  };

  const goPrev = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const renderQuestion = (question: any) => {
    const value = responses[question.id] || '';
    const selectedValues = Array.isArray(value) ? value : [];

    if (question.type === 'textarea') {
      return (
        <textarea
          value={(value as string) || ''}
          onChange={(e) => handleResponse(question.id, e.target.value)}
          placeholder={question.placeholder || ''}
          rows={4}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 resize-none focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
        />
      );
    }

    if (question.type === 'select') {
      return (
        <select
          value={(value as string) || ''}
          onChange={(e) => handleResponse(question.id, e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
        >
          <option value="">Sélectionnez...</option>
          {question.options?.map((opt: string) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    }

    if (question.type === 'multiselect') {
      return (
        <div className="space-y-2">
          {question.options?.map((opt: string) => {
            const isSelected = selectedValues.includes(opt);
            return (
              <label
                key={opt}
                className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                  isSelected ? 'border-bz-blue bg-bz-blue/5' : 'border-gray-300 bg-white hover:bg-gray-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => {
                    const current = selectedValues;
                    if (e.target.checked) {
                      handleResponse(question.id, [...current, opt]);
                    } else {
                      handleResponse(question.id, current.filter((v) => v !== opt));
                    }
                  }}
                  className="mt-1"
                />
                <span className="text-xs text-gray-700">{opt}</span>
              </label>
            );
          })}
          {question.maxSelections && (
            <p className="text-xs text-gray-500 italic">Sélectionnez jusqu&apos;à {question.maxSelections} options</p>
          )}
        </div>
      );
    }

    // Type text par défaut
    return (
      <input
        type="text"
        value={(value as string) || ''}
        onChange={(e) => handleResponse(question.id, e.target.value)}
        placeholder={question.placeholder || ''}
        className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
      />
    );
  };

  if (!isOpen) return null;

  if (!template) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
        <div className="bg-white rounded-xl p-6 max-w-md">
          <p className="text-red-600">Erreur: Template non trouvé</p>
          <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-200 rounded">
            Fermer
          </button>
        </div>
      </div>
    );
  }

  const CurrentIcon = currentPhase?.icon ? (iconMap[currentPhase.icon] || Shield) : Shield;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-6">
      <div className="w-[80vw] max-w-[1400px] max-h-[92vh] rounded-2xl bg-white shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shadow-inner">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">{template.name}</h2>
              <p className="text-[11px] text-white/80">{template.description}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5 text-white/80" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="px-6 pt-4 pb-2 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between text-[11px] text-gray-600 mb-1">
            <span>
              Étape {step} sur {maxSteps} • {currentPhase?.title || ''}
            </span>
            <span className="text-gray-500">Prise de besoin Politique de cybersécurité</span>
          </div>
          <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 transition-all"
              style={{ width: `${(step / maxSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 bg-gray-50 text-sm text-gray-700">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm text-gray-600">Chargement des données...</p>
            </div>
          ) : step === 1 ? (
            <>
              {/* Champs nom et description au début */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm mb-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Informations générales</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Client <span className="text-red-500">*</span>
                    </label>
                    {isLoadingClients ? (
                      <div className="px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-xs text-gray-500">
                        Chargement des clients...
                      </div>
                    ) : (
                      <select
                        value={selectedClientId}
                        onChange={(e) => setSelectedClientId(e.target.value ? parseInt(e.target.value, 10) : '')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
                        required
                      >
                        <option value="">Sélectionnez un client...</option>
                        {clients.map((client) => (
                          <option key={client.id} value={client.id}>
                            {client.nom}
                            {client.secteur ? ` (${client.secteur})` : ''}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Nom de la prise de besoin <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={nomBesoin}
                      onChange={(e) => setNomBesoin(e.target.value)}
                      placeholder="Ex: Politique de cybersécurité 2025"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Description (optionnel)
                    </label>
                    <textarea
                      value={descriptionBesoin}
                      onChange={(e) => setDescriptionBesoin(e.target.value)}
                      placeholder="Description de cette prise de besoin..."
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 resize-none focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t border-gray-100 mt-2">
                    <div className="flex items-start gap-2">
                      <input
                        id="showpriseclient"
                        type="checkbox"
                        checked={showPriseClient}
                        onChange={(e) => setShowPriseClient(e.target.checked)}
                        className="mt-1 h-4 w-4 text-bz-blue border-gray-300 rounded"
                      />
                      <label htmlFor="showpriseclient" className="text-xs text-gray-700">
                        Afficher cette prise de besoin dans l&apos;espace client
                        <span className="block text-[10px] text-gray-400">
                          Quand cette option est activée, le client peut voir ce besoin dans son portail PRIsME.
                        </span>
                      </label>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">
                        Prise de besoins réalisée par
                      </label>
                      <input
                        type="text"
                        value={makeBy}
                        onChange={(e) => setMakeBy(e.target.value)}
                        placeholder="Nom de la personne (ex: Daniel Bourassa)"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-xs text-gray-700 focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
                      />
                    </div>
                    <div className="flex items-start gap-2">
                      <input
                        id="archived"
                        type="checkbox"
                        checked={archived}
                        onChange={(e) => setArchived(e.target.checked)}
                        className="mt-1 h-4 w-4 text-red-500 border-gray-300 rounded"
                      />
                      <label htmlFor="archived" className="text-xs text-gray-700">
                        Archiver cette prise de besoin
                        <span className="block text-[10px] text-gray-400">
                          (utilisé pour masquer des prises anciennes sans les supprimer)
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Phase 1 */}
              {currentPhase && (
                <div className="space-y-4">
                  <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center">
                        <CurrentIcon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-red-600 uppercase">
                          Phase {step} sur {maxSteps}
                        </p>
                        <h3 className="text-base font-semibold text-gray-900">{currentPhase.title}</h3>
                        {currentPhase.description && (
                          <p className="text-xs text-gray-600 mt-1">{currentPhase.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="space-y-5">
                      {currentPhase.questions.map((q: any) => {
                        const questionLabel = q.label || '';
                        const questionId = q.id;
                        const questionType = q.type || 'text';
                        const questionOptions = q.options || [];
                        const questionPlaceholder = q.placeholder || '';
                        const questionRequired = q.required || false;
                        const questionHelpText = q.helpText;

                        return (
                          <div key={questionId} className="space-y-2">
                            <label className="block text-xs font-semibold text-gray-700">
                              {questionLabel}
                              {questionRequired && <span className="text-red-500 ml-1">*</span>}
                            </label>
                            {questionHelpText && (
                              <p className="text-[10px] text-gray-500 italic mb-1">{questionHelpText}</p>
                            )}
                            {renderQuestion({
                              id: questionId,
                              type: questionType,
                              options: questionOptions,
                              placeholder: questionPlaceholder,
                              required: questionRequired,
                            })}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : currentPhase ? (
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center">
                    <CurrentIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-red-600 uppercase">
                      Phase {step} sur {maxSteps}
                    </p>
                    <h3 className="text-base font-semibold text-gray-900">{currentPhase.title}</h3>
                    {currentPhase.description && (
                      <p className="text-xs text-gray-600 mt-1">{currentPhase.description}</p>
                    )}
                  </div>
                </div>
                <div className="space-y-5">
                  {currentPhase.questions.map((q: any) => {
                    const questionLabel = q.label || '';
                    const questionId = q.id;
                    const questionType = q.type || 'text';
                    const questionOptions = q.options || [];
                    const questionPlaceholder = q.placeholder || '';
                    const questionRequired = q.required || false;
                    const questionHelpText = q.helpText;

                    return (
                      <div key={questionId} className="space-y-2">
                        <label className="block text-xs font-semibold text-gray-700">
                          {questionLabel}
                          {questionRequired && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        {questionHelpText && (
                          <p className="text-[10px] text-gray-500 italic mb-1">{questionHelpText}</p>
                        )}
                        {renderQuestion({
                          id: questionId,
                          type: questionType,
                          options: questionOptions,
                          placeholder: questionPlaceholder,
                          required: questionRequired,
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500">Phase non trouvée</div>
          )}
        </div>

        {/* Footer nav */}
        <div className="px-6 py-3 bg-white border-t border-gray-200 flex items-center justify-between text-xs">
          <button
            onClick={step === 1 ? onClose : goPrev}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
          >
            {step === 1 ? (
              <>
                <X className="w-4 h-4" />
                Fermer
              </>
            ) : (
              <>
                <ChevronLeft className="w-4 h-4" />
                Étape précédente
              </>
            )}
          </button>
          <button
            onClick={step === maxSteps ? handleSave : goNext}
            disabled={isSaving}
            className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full bg-red-600 text-white font-semibold hover:bg-red-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {step === maxSteps ? (
              <>
                {isSaving ? 'Sauvegarde...' : 'Sauvegarder et terminer'}
                <CheckCircle2 className="w-4 h-4" />
              </>
            ) : (
              <>
                Étape suivante
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

